import { cn } from "@/lib/utils";

interface EyebrowLabelProps {
  children: React.ReactNode;
  align?: "left" | "center";
  className?: string;
}

/**
 * EyebrowLabel — flat editorial label that sits above a page H1.
 *
 * Replaces the rounded-pill Badge that previously preceded page headings.
 * No background, no border, no rounded shape — a hairline accent rail + a
 * monospace uppercase label. Reads like documentation, not marketing.
 *
 * Pattern: ──  USE CASE   (left-aligned, default)
 *          ── CENTER LABEL ──   (center-aligned)
 */
export default function EyebrowLabel({
  children,
  align = "left",
  className,
}: EyebrowLabelProps) {
  return (
    <div
      className={cn(
        "flex items-center gap-3 text-[11px] font-mono uppercase tracking-[0.22em] text-text-muted",
        align === "center" && "justify-center",
        className,
      )}
    >
      <span
        aria-hidden
        className="h-px w-8 shrink-0 bg-gradient-to-r from-transparent to-accent-2"
      />
      <span>{children}</span>
      {align === "center" && (
        <span
          aria-hidden
          className="h-px w-8 shrink-0 bg-gradient-to-l from-transparent to-accent-2"
        />
      )}
    </div>
  );
}
